/**
 * <AuthGate> — synchronously gates rendering of children on cerberus auth.
 *
 *  - status === 'loading' on a protected route → renders <loadingFallback>
 *  - status === 'unauthed' on a protected route → renders nothing
 *    (AuthProvider already triggered the redirect to /login)
 *  - status === 'authed' but no accepted-domain grant → renders <NoAccessScreen>
 *  - status === 'authed' with at least one accepted grant → renders children
 *  - public path (/login, /reset, /entrar, /verify) at any status →
 *    always renders children so the page can show the login form, the
 *    reset confirmation, the waitlist invite redemption form, or the
 *    email-verification confirmation to unauthed users. `/entrar` and
 *    `/verify` MUST be here (and in `redirect.ts`'s mirror
 *    `PUBLIC_PATHS`) — these pages run in the gap between "Cerberus
 *    account created" and "first authed login"; if they get gated,
 *    AuthProvider redirects to /login before the page can mount and
 *    the user can never finish the flow.
 *  - PWA offline edge case: when the device is offline AND the user is not
 *    yet authed (loading or unauthed), we render <OfflineSessionScreen>
 *    instead of a blank page or the redirect-to-/login path that would
 *    fail offline. See docs/pwa-update-and-offline.md §3.4.
 */
import { type ReactNode } from 'react'
import { useOnlineStatus } from '@/lib/offline/useOnlineStatus'
import { CERBERUS_ACCEPTED_DOMAINS } from './config'
import { NoAccessScreen } from './NoAccessScreen'
import { useAuth } from './useAuth'

function OfflineSessionScreen() {
  return (
    <div
      data-testid="cerberus-auth-offline"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
        color: '#1f2937',
      }}
    >
      <strong style={{ fontSize: 18, marginBottom: 8 }}>Sem conexão</strong>
      <span style={{ fontSize: 14, maxWidth: 320, lineHeight: 1.5 }}>
        Não conseguimos validar sua sessão. Reconecte sua internet e tente
        novamente.
      </span>
    </div>
  )
}

const PUBLIC_PATHS: ReadonlySet<string> = new Set([
  '/login',
  '/reset',
  '/entrar',
  '/verify',
  // Phase 8 (M8) — mobile-web-minimal public surfaces.
  // `/` is dual-mode: HomePage internally chooses the install gate
  // (`<InstallCTA webContinueHref="/login" />`) for unauthed visitors vs
  // the desktop/mobile dashboard for authed ones. (The old marketing
  // `LandingPublic` was retired — the unauthed root is now the InstallCTA
  // gate; see src/modules/finance/HomePage.tsx.)
  '/',
  '/instalar',
])

/** Public prefixes (e.g. `/share/<token>`). */
const PUBLIC_PREFIXES: readonly string[] = ['/share/']

function isPublicPath(): boolean {
  if (typeof window === 'undefined') return false
  const p = window.location.pathname
  if (PUBLIC_PATHS.has(p)) return true
  return PUBLIC_PREFIXES.some((pref) => p.startsWith(pref))
}

interface AuthGateProps {
  children: ReactNode
  loadingFallback?: ReactNode
}

export function AuthGate({ children, loadingFallback }: AuthGateProps) {
  const { status, user, hasGrant } = useAuth()
  const online = useOnlineStatus()

  if (isPublicPath()) return <>{children}</>

  // PWA offline guard: if the device is offline and the user is not yet
  // confirmed authed, show a dedicated "no connection" screen instead of
  // a blank page (status==='unauthed' returns null while AuthProvider
  // tries a redirect that cannot navigate offline) or a stranded
  // spinner. Once the user reconnects, AuthProvider resumes and this
  // path stops matching.
  if (!online && (status === 'loading' || status === 'unauthed' || !user)) {
    return <OfflineSessionScreen />
  }

  if (status === 'loading') {
    return (
      <>
        {loadingFallback ?? (
          <div
            data-testid="cerberus-auth-loading"
            className="p-4 text-muted text-sm"
          >
            Carregando...
          </div>
        )}
      </>
    )
  }

  if (status === 'unauthed' || !user) {
    // AuthProvider already kicked off window.location.assign.
    return null
  }

  const hasAnyAcceptedGrant =
    user.is_super_admin ||
    CERBERUS_ACCEPTED_DOMAINS.some((d) => hasGrant(d))
  if (!hasAnyAcceptedGrant) {
    return <NoAccessScreen />
  }

  return <>{children}</>
}
